/**
 * Shared empty-response handling choices for dataset import.
 *
 * Multilabel classification: none_selected (default) | unlabeled | generate_with_ai | exclude
 * All other task types: unlabeled (default) | generate_with_ai | exclude
 *
 * generate_with_ai keeps existing responses and runs LABEL_ONLY on empty rows only.
 */

import { hasLabelsNouns } from '@/features/datasets/utils/hasLabelsHandling';

export const EMPTY_RESPONSES_NONE_SELECTED = 'none_selected';
export const EMPTY_RESPONSES_UNLABELED = 'unlabeled';
export const EMPTY_RESPONSES_GENERATE_WITH_AI = 'generate_with_ai';
export const EMPTY_RESPONSES_EXCLUDE = 'exclude';

/** True when empty cells should be imported unlabeled and filled by LABEL_ONLY. */
export function isEmptyGenerateWithAi(value) {
    return value === EMPTY_RESPONSES_GENERATE_WITH_AI;
}

const NONE_SELECTED_OPTION = {
    value: EMPTY_RESPONSES_NONE_SELECTED,
    title: 'None Selected',
    description:
        'Import with the None Selected label. These samples are included in training as a valid response category.',
};

/** Task-type nouns used in section copy (singular / column / plural-ish). */
export function emptyResponseNouns(taskType) {
    switch (taskType) {
        case 'EntityRecognitionResponse':
        case 'NERResponse':
            return {
                valuePlural: 'entity values',
                column: 'entity column',
                unit: 'an entity',
                unitPlural: 'entities',
                until: 'they are annotated',
                excludeUnit: 'an entity',
            };
        case 'Summarization':
            return {
                valuePlural: 'summary values',
                column: 'summary column',
                unit: 'a summary',
                unitPlural: 'a summary',
                until: 'they have a summary',
                excludeUnit: 'a summary',
            };
        case 'ContentGeneration':
            return {
                valuePlural: 'output values',
                column: 'output column',
                unit: 'an output',
                unitPlural: 'an output',
                until: 'they have an output',
                excludeUnit: 'an output',
            };
        default:
            return {
                valuePlural: 'label values',
                column: 'label column',
                unit: 'a label',
                unitPlural: 'a label',
                until: 'they are labeled',
                excludeUnit: 'a label',
            };
    }
}

export function emptyResponseDefault(isMultilabel) {
    return isMultilabel ? EMPTY_RESPONSES_NONE_SELECTED : EMPTY_RESPONSES_UNLABELED;
}

export function emptyResponseOptions({
    isMultilabel = false,
    taskType,
    includeGenerateWithAi = true,
} = {}) {
    const nouns = emptyResponseNouns(taskType);
    const aiNouns = hasLabelsNouns(taskType);
    const unlabeled = {
        value: EMPTY_RESPONSES_UNLABELED,
        title: 'Mark as Unlabeled',
        description: `Import without ${nouns.unitPlural}. These samples are excluded from training until ${nouns.until}.`,
    };
    const generateWithAi = {
        value: EMPTY_RESPONSES_GENERATE_WITH_AI,
        title: `Generate ${aiNouns.plural} with AI`,
        description: `Keep existing ${aiNouns.plural} and let AI generate ${aiNouns.plural} for empty values after upload.`,
    };
    const exclude = {
        value: EMPTY_RESPONSES_EXCLUDE,
        title: 'Exclude from import',
        description: `Skip rows that do not contain ${nouns.excludeUnit}.`,
    };
    const rest = includeGenerateWithAi
        ? [unlabeled, generateWithAi, exclude]
        : [unlabeled, exclude];
    if (isMultilabel) {
        return [NONE_SELECTED_OPTION, ...rest];
    }
    return rest;
}

export function emptyResponseSectionCopy(taskType) {
    const nouns = emptyResponseNouns(taskType);
    return {
        title: `How should empty ${nouns.valuePlural} be handled?`,
        description: `In case of empty ${nouns.valuePlural} in your ${nouns.column}, choose how to treat these samples.`,
        tooltip: `Choose how to treat rows whose ${nouns.column} is empty.`,
    };
}

/**
 * Whether to show the "How should empty … be handled?" radios.
 *
 * The Hugging Face empty-values scan starts unsettled. Treating that window as
 * "show" flashes the radios and then hides them when the column has no empties.
 * Show only after the scan settles:
 * - true  → empties found → show
 * - false → none found → hide
 * - null after settle → unverifiable → show (same fallback as file upload)
 */
export function shouldShowEmptyResponsesChoice({
    hasLabelColumn,
    emptyValuesCheckSettled,
    hasEmptyValues,
} = {}) {
    if (!hasLabelColumn || !emptyValuesCheckSettled) {
        return false;
    }
    return hasEmptyValues !== false;
}
